import { Component, ElementRef, ErrorHandler, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SectionsService } from '../../../services/sections.service';
import { BaseComponent } from '../../../shared/utils/base.component';
import { ReferralDataService } from '../../../services/referral-data.service';
import { StepInfo } from '../../../interfaces/referralSections.interface';
import {
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { debounceTime } from 'rxjs';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import { ButtonComponent } from '@ppl/ui';
import { NotificationService } from '../../../shared/services/notification.service';
import { ADDITIONAL_ATTRIBUTE_MAP, ENROLLMENT_TITLE_MAPPINGS, ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS, getProviderClassificationForm } from '../../../shared/constants/static-data.constants';
import { CheckListService } from '../../../services/check-list.service';
import { mapParentGuardiansForSave, missingParentDocCodes } from '../participant-information/parent-guardian/parent-guardian.util';
import { deriveProviderType, PROVIDER_CLASSIFICATION_CONTROLS } from '../../../shared/components/provider-classification-questions/provider-classification-questions.component';
import { UserService } from '../../../services/user.service';
import { StorageService } from '../../../services/storage.service';
import { LeadDetailsService } from '../../../services/lead-details.service';
import { KeyboardSelectDirective } from '../../../shared/directives/keyboard-select.directive';
import { CommonModule } from '@angular/common';
import { buildDuplicateIdentifierFieldErrors } from '../../../shared/utils/duplicate-identifier.util';

@Component({
  selector: 'ppl-steps',
  standalone: true,
  imports: [
    CommonModule,
    NzCardModule,
    NzStepsModule,
    NzButtonModule,
    ButtonComponent,
    KeyboardSelectDirective
  ],
  templateUrl: './steps.component.html',
  styleUrl: './steps.component.css',
})
export class StepsComponent extends BaseComponent implements OnInit, OnDestroy {
  @Output() stepSelected = new EventEmitter<any>();
  @Output() showConfirmation = new EventEmitter<any>();
  @Output() showTrackingInformation = new EventEmitter<any>();
  @Output() stepEnter = new EventEmitter<string>();
  @ViewChild('footerButtons', { read: ElementRef }) footerButtonsRef?: ElementRef<HTMLButtonElement>;

  currentStep = 0;
  availableSteps: StepInfo[] = [];
  mandatorySteps: any[] = [];
  onSummary = false;
  identifier: any[] = [];
  additionalAttributes: any[] = [];
  communicationType: any[] = [];
  error: any = {};
  step = '';
  selectedStep: any;
  isStepSelected = false;
  program: any;
  flow: any;

  constructor(
    private sectionsService: SectionsService,
    private referralDataService: ReferralDataService,
    private notification: NzNotificationService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private notificationService: NotificationService,
    private userService: UserService,
    private storageService: StorageService,
    private checkListService: CheckListService,
    private leadDetailsService: LeadDetailsService,
    private errorHandler: ErrorHandler
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.currentStep = 0;
  }

  ngOnInit(): void {
    this.currentStep = 0;
    this.program = this.userService.getSelectedProgram();
    const code = this.program.program_code;
    const flow = this.sectionsService.getFlow();
    if (flow === 'providerEnrollment') {
      this.data = this.sectionsService.getProviderSteps(code);
    } else {
      this.data = this.sectionsService.getAllSteps(code);
    }
    this.availableSteps = this.data.filter(
      (step) => step.isAvailable === 'Y'
    );
    this.mandatorySteps = this.data.filter(
      (step) => step.isMandatory === 'Y'
    );
    this.flow = this.sectionsService.getFlow();
    this.stepSelected.emit(this.availableSteps[0]);
    this.identifier = this.lookupService.getLookupValue(
      'Identifier_type'
    );
    this.communicationType =
      this.lookupService.getLookupValue('Communication_Type');

    if (this.storageService.getTrackingNumber() && this.storageService.getProviderPPLId()) {
      this.showTrackingInformation.emit({
        trackingNumber: this.storageService.getTrackingNumber(),
        providerPPLId: this.storageService.getProviderPPLId()
      })
    }

    if (this.storageService.getTrackingNumber() && this.storageService.getParticipantPPLId()) {
      this.showTrackingInformation.emit({
        trackingNumber: this.storageService.getTrackingNumber(),
        participantPPLId: this.storageService.getParticipantPPLId()
      })
    }

    this.referralDataService.validationError
      .pipe(debounceTime(500))
      .subscribe((val: any) => {
        if (Object.keys(val).length !== 0) {
          event?.preventDefault();
          // resetting the values to make sure when there are validations the values are not preserved.
          this.step = '';
          this.isStepSelected = false;
          return;
        } else {
          this.error[this.availableSteps[this.currentStep].step_id] = true;
          if (
            (this.availableSteps[this.currentStep].step_id == 'contact_details' && ['providers'].includes(this.flow))
            || (this.currentStep == 0 && ['participants', 'referral'].includes(this.flow))
          ) {
            const load =
              this.sectionsService.getFlow() === 'participants'
                ? 'participantInfoData'
                : this.sectionsService.getFlow() === 'providers'
                  ? 'dupsCheckData'
                  : 'referralInfoData';
            const payload = {
              [load]: {
                ...this.formatInfoData(
                  this.referralDataService.getReferralFormData().infoData
                ),
                email: this.referralDataService.getReferralFormData()?.contactDetailsData?.email,
                mobile: this.referralDataService.getReferralFormData()?.contactDetailsData?.mobile,
                altPhone: this.referralDataService.getReferralFormData()?.contactDetailsData?.altPhone,
                state:
                  this.referralDataService.getReferralFormData().state?.state ||
                  '',
                ...((this.sectionsService.getFlow() !== 'participants' || this.sectionsService.getFlow() !== 'providers') && {
                  programCode: this.program.program_code,
                  referralId: this.referralDataService.getReferralDetails('infoData')?.referralId || 0,
                }),
              },
            };
            const type =
              this.sectionsService.getFlow() === 'participants'
                ? 'participant'
                : this.sectionsService.getFlow() === 'providers'
                  ? 'provider'
                  : 'referral_participant';
            // Field-level duplicate-identifier errors (ITIN/CIN) are only surfaced for the
            // participant referral Basic Information step; other flows keep the generic error toast.
            const isParticipantReferral = type === 'referral_participant';
            this.referralDataService
              .checkDups(type, payload)
              .subscribe({
                next: (response: any) => {
                  // The gateway can relay the duplicate-identifier 406 body on the success channel.
                  const duplicateFieldErrors = isParticipantReferral
                    ? buildDuplicateIdentifierFieldErrors(response)
                    : {};
                  if (Object.keys(duplicateFieldErrors).length) {
                    this.referralDataService.duplicateIdentifierError.next(duplicateFieldErrors);
                    return;
                  }
                  this.saveData();
                },
                error: (err: unknown) => {
                  const duplicateFieldErrors = isParticipantReferral
                    ? buildDuplicateIdentifierFieldErrors((err as { error?: unknown })?.error)
                    : {};
                  if (Object.keys(duplicateFieldErrors).length) {
                    this.referralDataService.duplicateIdentifierError.next(duplicateFieldErrors);
                    return;
                  }
                  this.errorHandler.handleError(err);
                },
              });
          } else {
            if (this.referralDataService.getChanged()) {
              this.saveData();
            }
            else {
              this.setStep();
            }
          }
        }
      });
    this.referralDataService.stepValidation
      .pipe(debounceTime(500))
      .subscribe((val: any) => {
        this.error[val.step] = val.val;
        this.setStep();
      })
  }

  checkIfNext(isDirty: any, isUpdated: any) {
    if (
      !this.referralDataService.getReferralFormData()[isDirty] &&
      !this.referralDataService.getReferralFormData()[isUpdated]
    ) {
      this.error[this.availableSteps[this.currentStep].step_id] = false;
      if (this.flow === 'participantEnrollment' && !this.storageService.getParticipantId()) {
        this.notificationService.alert('error', "Error", 'Please save basic information before going forward.')
      }
      else if (this.flow === 'providerEnrollment' && !this.storageService.getProviderId()) {
        this.notificationService.alert('error', "Error", 'Please save basic information before going forward.')
      }
      else {
        // Summary has no validations. upload_docs now routes through setClick so its component can
        // validate required parent/guardian documents (PAR1..PARn) on Next; Prev is still handled by
        // the prev branch below.
        if (this.availableSteps[this.currentStep].step_id === 'summary') {
          this.setStep();
        }
        else {
          // need to check whether user is clicking on previous step or not. if user is navigating
          // to previous step we should allow the user to navigate.
          var currentStep = this.availableSteps[this.currentStep];
          const currentSelectedStep = this.isStepSelected ? this.availableSteps.find(as => as.step_id === this.selectedStep) : null;
          if (this.step === 'prev' || (currentSelectedStep && currentSelectedStep?.stepname_id < currentStep.stepname_id)) {
            this.referralDataService.setPreviousClick(this.availableSteps[this.currentStep].step_id);
            this.setStep();
          }
          else {
            this.referralDataService.setClick(this.availableSteps[this.currentStep].step_id);
          }
        }
      }
    } else {
      // need to check whether user is clicking on previous step or not. if user is navigating
      // to previous step we should allow the user to navigate.
      var currentStep = this.availableSteps[this.currentStep];
      const currentSelectedStep = this.isStepSelected ? this.availableSteps.find(as => as.step_id === this.selectedStep) : null;
      if (this.step === 'prev' || (currentSelectedStep && currentSelectedStep?.stepname_id < currentStep.stepname_id)) {
        this.referralDataService.setPreviousClick(this.availableSteps[this.currentStep].step_id);
        this.setStep();
      }
      else {
        this.referralDataService.setClick(this.availableSteps[this.currentStep].step_id);
      }
    }
  }

  checkPageValidation() {
    this.referralDataService.setClick(this.availableSteps[this.currentStep].step_id);
  }

  next(): void {
    this.step = 'next';
    this.checkIfRedirect();
    this.stepEnter.emit();
  }

  focusNextButton() {
    if (this.footerButtonsRef?.nativeElement) {
      const allButtons = this.footerButtonsRef.nativeElement.querySelectorAll('button');
      const visibleButtons = Array.from(allButtons).filter(
        (button: HTMLElement) => !button.hasAttribute('disabled') && !button.getAttribute('aria-hidden')
      );
      const lastButton = visibleButtons[visibleButtons.length - 1] as HTMLElement;
      if (lastButton) {
        lastButton.focus();
      }
    }
  }

  submitReferral() {
    console.log('Submitting Referral');
  }

  isFormValid() {
    // const stepsToValidate = this.availableSteps.filter(
    //   (stepId) =>
    //     stepId.step_id !== 'summary' && stepId.step_id !== 'upload_docs'
    //     && stepId.step_id !== 'signatures' && stepId.step_id !== 'checklist'
    //     && stepId.step_id !== 'forms' && stepId.step_id !== 'associations'
    // );
    return this.mandatorySteps.every(
      (stepId) => this.error[stepId.step_id] === true
    );
  }

  checkIfRedirect(): void {
    const stepId = this.availableSteps[this.currentStep].step_id;
    switch (stepId) {
      case 'participant_info':
        this.checkIfNext('isInfoFormDirty', 'isInfoFormUpdated');
        break;
      case 'service_details':
        this.checkIfNext('isServiceFormDirty', 'isServiceFormUpdated');
        break;
      case 'contact_details':
        this.checkIfNext('isContactFormDirty', 'isContactFormUpdated');
        break;
      case 'comm_preferences':
        this.checkIfNext('isCommFormDirty', 'isCommFormUpdated');
        break;
      case 'upload_docs':
        this.checkIfNext('isUploadFormDirty', 'isUploadFormUpdated');
        break;
      case 'associations':
        this.checkPageValidation();
        break;
      case 'signatures':
        this.checkPageValidation();
        break;
      case 'consumer_rates':
        this.checkIfNext(false, false);
        break;
      case 'service_credentials':
        this.checkIfNext(false, false);
        break;
      case 'forms':
        this.checkPageValidation();
        break;
      case 'checklist':
        this.checkPageValidation();
        break;
      case 'payment_details':
        this.checkPageValidation();
        break;
      case 'summary':
        this.checkIfNext(false, false);
        break;
      default:
        console.error(`Unhandled step ID: ${stepId}`);
        break;
    }
  }

  /**
   * Persists the Provider Classification answers captured on the basic details step.
   * They live in a checklist form rather than the enrollment record, so they go through
   * the provider forms endpoint using the provider id the info save just returned.
   */
  saveProviderClassification(infoData: any, providerId: any, enrollmentTypes: string[]): void {
    const backendTypes = (enrollmentTypes ?? [])
      .map((type: string) => ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS[type])
      .filter(Boolean);
    const providerType = backendTypes.includes('VE')
      ? 'VE'
      : backendTypes.includes('IC')
        ? 'IC'
        : backendTypes.includes('IF')
          ? 'IF'
          : '';

    if (!providerType || !providerId || !infoData?.servesAll) {
      return;
    }

    const classificationForm = getProviderClassificationForm(providerType);
    const fields = classificationForm.fields;
    // Initial Filing Date isn't one of the classification questions, so it only ever went out
    // empty. Vendor Classification carries the provider type the answers resolve to — the same
    // shape the provider detail page saves.
    const formValue: Record<string, any> = {
      [fields.servesAll]: infoData.servesAll ?? '',
      [fields.vendored]: infoData.vendored ?? '',
      [fields.followsProgramDesign]: infoData.followsProgramDesign ?? '',
      [fields.vendorNumber]: infoData.vendorNumber ?? '',
      [fields.vendorClassification]: deriveProviderType(infoData),
    };

    const payload = {
      type: 'provider',
      checklistItems: [
        {
          checklistId: classificationForm.checklistItemId,
          checklistDetailId: classificationForm.checklistItemDetailId,
          participantId: '',
          providerId: Number(providerId),
          providerType,
          associationId: [],
          formValue,
        },
      ],
    };

    this.checkListService
      .saveParticipantProviderForms(this.program.program_code, providerId, false, payload)
      .subscribe({
        error: (err) => {
          console.error('Error saving provider classification', err);
        },
      });
  }

  saveData() {
    const onStep = this.availableSteps[this.currentStep].step_id;
    if (this.flow === 'participantEnrollment') {
      if (onStep == 'participant_info') {
        const infoData = this.referralDataService.getReferralFormData().infoData;
        const formattedInfoData = infoData && this.formatInfoData(infoData);
        const finalInfoPayload = {
          ...formattedInfoData,
          participantId: this.storageService.getParticipantId(),
          programCode: this.program.program_code,
          trackingNumber: this.storageService.getTrackingNumber(),
          participantPPLId: this.storageService.getParticipantPPLId()
        };
        this.referralDataService.saveInfoData(finalInfoPayload, true, true).subscribe((data) => {
          if (data.status == 200) {
            this.setStep();
            this.storageService.setParticipantId(data.responsedata.participantId);
            this.storageService.setParticipantPPLId(data.responsedata.participantPPLId);
            this.storageService.setTrackingNumber(data.responsedata.trackingNumber);
            this.refreshRelationshipIds(data.responsedata.participantId);
            this.showTrackingInformation.emit({
              trackingNumber: data.responsedata.trackingNumber,
              participantPPLId: data.responsedata.participantPPLId
            })
          }
        })
      }
      else if (onStep == 'comm_preferences') {
        const commPreferencesData = this.referralDataService.getReferralFormData().commPreferencesData;
        const formattedCommPreferencesData = commPreferencesData && this.formatCommPreferencesData(commPreferencesData);
        const finalCommPreferencesPayload = {
          ...formattedCommPreferencesData,
          programCode: this.program.program_code,
          participantId: this.storageService.getParticipantId()
        }
        this.referralDataService.saveCommPref(finalCommPreferencesPayload, true, true).subscribe((data) => {
          if (data.status === 200) {
            this.setStep();
          }
        })
      }
      else if (onStep == 'contact_details') {
        const contactDetailsData = this.referralDataService.getReferralFormData().contactDetailsData;
        const formattedContactDetailsData = contactDetailsData && this.formatContactDetailsData(contactDetailsData);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { preferredMethod, email, altPhone, mobile, residentGt18, yearsInState, ...communicationData } = formattedContactDetailsData;
        const finalContactDetailsPayload = {
          communicationData,
          programCode: this.program.program_code,
          participantId: this.storageService.getParticipantId(),
          referralId: this.referralDataService.getReferralDetails('infoData')?.referralId
        }
        this.referralDataService.saveContactDetails(finalContactDetailsPayload, true, true).subscribe((data: any) => {
          if (data.status == 200) {
            this.setStep();
          }
        })
      }
      else {
        this.setStep();
      }
    }
    else if (this.flow === 'providerEnrollment') {
      if (onStep == 'participant_info') {
        const infoData = this.referralDataService.getReferralFormData().infoData;
        const formattedInfoData = infoData && this.formatInfoData(infoData);
        // formattedInfoData["applicationStatus"] = "In Progress";
        const providerTypes = this.sectionsService.getEnrollmentType();
        formattedInfoData["providerType"] = ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS[providerTypes[0]];

        // Provider Classification answers ride on the info step's form but belong to a
        // checklist form, so they are saved separately and kept out of this payload.
        const classificationAnswers = { ...infoData };
        PROVIDER_CLASSIFICATION_CONTROLS.forEach((name) => delete formattedInfoData[name]);

        const finalInfoPayload = {
          ...formattedInfoData,
          providerId: this.storageService.getProviderId(),
          trackingNumber: this.storageService.getTrackingNumber(),
          programCode: this.program.program_code,
          providerPPLId: this.storageService.getProviderPPLId()
        };
        this.referralDataService.saveInfoData(finalInfoPayload, false, true).subscribe((data) => {
          if (data.status == 200) {
            this.setStep();
            this.storageService.setProviderId(data.responsedata.providerId);
            this.storageService.setTrackingNumber(data.responsedata.trackingNumber);
            this.storageService.setProviderPPLId(data.responsedata.providerPPLId);
            this.saveProviderClassification(classificationAnswers, data.responsedata.providerId, providerTypes);
            this.showTrackingInformation.emit({
              trackingNumber: data.responsedata.trackingNumber,
              providerPPLId: data.responsedata.providerPPLId
            })
          }
        })
      }
      else if (onStep == 'service_details') {
        const serviceDetailsData = this.referralDataService.getReferralFormData().serviceDetailsData;
        const serviceDetailsFormattedData = serviceDetailsData && this.formatServiceDetailsData(serviceDetailsData);
        const serviceDetailsPayload = {
          ...serviceDetailsFormattedData,
          programCode: this.program.program_code,
          providerId: this.storageService.getProviderId(),
          trackingNumber: this.storageService.getTrackingNumber(),
        }
        this.referralDataService.saveServiceDetails(serviceDetailsPayload, true).subscribe((data) => {
          if (data.status === 200) {
            this.setStep();
          }
        })
      }
      else if (onStep == 'comm_preferences') {
        const commPreferencesData = this.referralDataService.getReferralFormData().commPreferencesData;
        const formattedCommPreferencesData = commPreferencesData && this.formatCommPreferencesData(commPreferencesData);
        const finalCommPreferencesPayload = {
          ...formattedCommPreferencesData,
          programCode: this.program.program_code,
          providerId: this.storageService.getProviderId(),
          trackingNumber: this.storageService.getTrackingNumber(),
        }
        this.referralDataService.saveCommPref(finalCommPreferencesPayload, false, true).subscribe((data) => {
          if (data.status === 200) {
            this.setStep();
          }
        })
      }
      else if (onStep == 'contact_details') {
        const contactDetailsData = this.referralDataService.getReferralFormData().contactDetailsData;
        const formattedContactDetailsData = contactDetailsData && this.formatContactDetailsData(contactDetailsData);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { preferredMethod, email, altPhone, mobile, residentGt18, yearsInState, ...communicationData } = formattedContactDetailsData;
        const finalContactDetailsPayload = {
          communicationData,
          programCode: this.program.program_code,
          providerId: this.storageService.getProviderId(),
          trackingNumber: this.storageService.getTrackingNumber(),
        }
        this.referralDataService.saveContactDetails(finalContactDetailsPayload, false, true).subscribe((data) => {
          if (data.status === 200) {
            this.setStep();
          }
        })
      }
      else {
        this.setStep();
      }
    }
    else {
      this.setStep();
    }
  }

  setStep() {
    if (this.step == 'next') {
      this.currentStep++;
    } else if (this.step == 'prev') {
      this.currentStep--;
    }
    this.onSummary =
      this.currentStep >= this.availableSteps.length - 1 ? true : false;
    let step;
    if (this.isStepSelected) {
      step = this.availableSteps.find(
        (step) => step.step_id == this.selectedStep
      );
      this.currentStep = this.getStepIndex(this.selectedStep);
      this.onSummary = this.selectedStep == 'summary' ? true : false;
    } else {
      step = this.availableSteps[this.currentStep];
    }
    this.stepSelected.emit(step);
    if (this.onSummary) {
      this.mandatorySteps.forEach(step => {
        if (!this.error[step.step_id]) {
          this.error[step.step_id] = 'noVal';
        } else if (this.error[step.step_id] !== true) {
          this.error[step.step_id] = 'noVal';
        } else {
          this.error[step.step_id] = true;
        }
      });
    }
    this.isStepSelected = false;
    this.step = '';
  }

  goToStep(selectedStep: string) {
    this.isStepSelected = true;
    this.selectedStep = selectedStep;
    // this.onSummary = (selectedStep == 'summary') ? true : false;
    this.checkIfRedirect();
  }

  onStepKeyboardSelect(event: KeyboardEvent, stepKey: string) {
    event.preventDefault();
    this.goToStep(stepKey);
    this.stepEnter.emit(stepKey);
  }

  getStepStatus(step: string) {
    return this.availableSteps[this.currentStep].step_id == step
      ? 'process'
      : this.error[step] == true
        ? 'finish'
        : this.error[step] == 'noVal'
          ? 'error'
          : 'wait';
  }

  getStepIndex(id: string): number {
    return this.availableSteps.map((step) => step.step_id).indexOf(id);
  }

  prev(): void {
    this.onSummary = false;
    this.step = 'prev';
    this.checkIfRedirect();
    this.stepEnter.emit();
  }

  formatDateTimes(serviceDetailsData: any) {
    return {
      availabilityTimingStart:
        this.dateTimeFormatterService.formatDate(
          serviceDetailsData.availabilityTimingStart,
          'hh:mm a'
        ) || '',
      availabilityTimingEnd:
        this.dateTimeFormatterService.formatDate(
          serviceDetailsData.availabilityTimingEnd,
          'hh:mm a'
        ) || '',
      dayEmergencyAvailablityStart:
        this.dateTimeFormatterService.formatDate(
          serviceDetailsData.dayEmergencyAvailablityStart,
          'hh:mm a'
        ) || '',
      dayEmergencyAvailablityEnd:
        this.dateTimeFormatterService.formatDate(
          serviceDetailsData.dayEmergencyAvailablityEnd,
          'hh:mm a'
        ) || '',
      nightEmergencyAvailablityStart:
        this.dateTimeFormatterService.formatDate(
          serviceDetailsData.nightEmergencyAvailablityStart,
          'hh:mm a'
        ) || '',
      nightEmergencyAvailablityEnd:
        this.dateTimeFormatterService.formatDate(
          serviceDetailsData.nightEmergencyAvailablityEnd,
          'hh:mm a'
        ) || '',
    };
  }

  formatServiceDetailsData(serviceDetailsData: any) {
    serviceDetailsData.prospectiveParticipants = serviceDetailsData.prospectiveParticipants.filter((participant: any) => {
      return !(participant.firstName.trim() === ''
        && participant.lastName.trim() === ''
        && participant.phoneNum === '')
    });
    serviceDetailsData.prospectiveParticipants.forEach((participant: any) => {
      participant.firstName = participant.firstName?.trim();
      participant.lastName = participant.lastName?.trim();
    });
    return {
      ...serviceDetailsData,
      ...this.formatDateTimes(serviceDetailsData),
      profLicences: serviceDetailsData.profLicences.map((licence: string) => ({
        licenseType: licence,
      })),
      servicesRendered: serviceDetailsData.servicesRendered?.map(
        (service: string) => ({ serviceType: service })
      ),
      isAvailableForEmergencies:
        serviceDetailsData.isAvailableForEmergencies?.toLowerCase() === 'yes',
      filesUploaded: serviceDetailsData.filesUploaded.map((file: any) => ({
        leadFileId: file.id,
      })),
    };
  }

  formatCommPreferencesData(commPreferencesData: any) {
    if (!commPreferencesData.formLanguage) {
      commPreferencesData.formLanguage = 'EN';
    }
    return {
      ...commPreferencesData,
      ...this.formatDateTimes(commPreferencesData),
      emergencyContactFirstName: commPreferencesData.emergencyContactFirstName?.trim(),
      emergencyContactLastName: commPreferencesData.emergencyContactLastName?.trim(),
      spokenLanguages: commPreferencesData.spokenLanguages.map(
        (lang: string) => ({ language: lang })
      ),
      isAvailableForEmergencies: commPreferencesData.isAvailableForEmergencies?.toLowerCase() === 'yes',
      startContactTime:
        this.dateTimeFormatterService.formatDate(
          commPreferencesData.startContactTime,
          'hh:mm a'
        ) || '',
      endContactTime:
        this.dateTimeFormatterService.formatDate(
          commPreferencesData.endContactTime,
          'hh:mm a'
        ) || '',
    };
  }

  formatInfoData(infoData: any) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isProvidingServices, showOnDirectory, SSN, CIN, EIN, TIN, relationshipInformation, ...restInfoData } = infoData;
    return {
      ...restInfoData,
      fname: infoData.fname?.trim(),
      lname: infoData.lname?.trim(),
      fiOrgName: infoData.fiOrgName != null ? infoData.fiOrgName != 'Other' ? infoData.fiOrgName : infoData.newfiOrgName ? infoData.newfiOrgName : infoData.fiOrgName : null,
      identifiers: this.getIdentifiers(infoData, this.identifier),
      additionalAttributes: this.getadditionalAttributes(infoData, ADDITIONAL_ATTRIBUTE_MAP),
      ...((isProvidingServices !== undefined && isProvidingServices !== null) && {
        isProvidingServices: isProvidingServices?.toLowerCase() === 'yes',
      }),
      ...(showOnDirectory !== undefined && {
        isShowOnDirectory: showOnDirectory?.toLowerCase() === 'yes',
      }),
      ...(relationshipInformation !== undefined && {
        relationshipInformation: this.formatRelationshipInformation(relationshipInformation),
      })
    };
  }

  // Parent/guardian records for minor participants — shared mapping (drops NA/empty rows, strips
  // transient flags, normalizes id + dob, flattens address). See parent-guardian.util.
  formatRelationshipInformation(relationshipInformation: any[]): any[] {
    return mapParentGuardiansForSave(relationshipInformation);
  }

  /**
   * Parent/guardian rows go out with id:0 for new records (id>0 means update). The save response
   * returns the created ids but not which row each belongs to, and mapParentGuardiansForSave drops
   * NA/empty rows so the arrays are not index-aligned — so re-read the participant instead and let
   * the ids arrive attached to their own records.
   *
   * Without this the in-memory infoData keeps id:0, and returning to the step and saving again
   * creates a second set of parents.
   */
  private refreshRelationshipIds(participantId: number): void {
    if (!participantId || !this.program?.program_code) {
      return;
    }

    // The FormArray is seeded with two rows for every participantEnrollment participant, minor or
    // not, so a raw length check is always 2 and proves nothing. Run the same mapping the payload
    // uses (drops NA/empty rows) and only re-read when a row actually went out as new (id 0) and
    // needs its persisted id back.
    const info = this.referralDataService.getReferralFormData()?.infoData;
    const savedRows = mapParentGuardiansForSave(info?.relationshipInformation ?? []);
    if (!savedRows.some((rec) => !rec.id)) {
      return;
    }

    this.leadDetailsService
      .fetchLeadDetailsApi('participantEnrollment', this.program.program_code, participantId, true)
      .subscribe({
        next: (data: any) => {
          const saved = data?.responsedata?.infoData?.relationshipInformation;
          if (!Array.isArray(saved)) {
            return;
          }
          this.referralDataService.setReferralDetails(data.responsedata);
          // Re-read infoData rather than closing over the earlier reference — the user may have
          // edited another field while the fetch was in flight.
          const current = this.referralDataService.getReferralFormData()?.infoData ?? {};
          this.referralDataService.setReferralFormData('infoData', {
            ...current,
            relationshipInformation: saved,
          });
        },
        error: () => {
          // Leave the store alone; the next full lead load will bring the persisted ids.
        },
      });
  }

  formatContactDetailsData(contactDetailsData: any) {
    return {
      ...contactDetailsData,
      contactMethods: this.getContactMethods(
        contactDetailsData,
        this.communicationType
      ),
    };
  }

  submit(actionPerformed?: string) {
    const flow = this.sectionsService.getFlow();

    // Registration gate: a minor participant must have a supporting document (PAR1..PARn) for each
    // parent/guardian before enrollment can be submitted.
    if (flow === 'participantEnrollment') {
      const info = this.referralDataService.getReferralFormData()?.infoData;
      const relationshipInformation = info?.relationshipInformation || [];
      const uploadedDocTypes = this.referralDataService.getReferralFormData()?.uploadedDocTypes || [];
      // Adults are exempt even when relationship records exist — upload-docs hides the PAR doc
      // types for them, so this gate would otherwise be impossible to satisfy.
      const missing = missingParentDocCodes(relationshipInformation, uploadedDocTypes, this.isMinor(info?.dob));
      if (missing.length) {
        const docTypeList = this.lookupService.getLookupValue('Upload_Doc_Type');
        const labels = missing.map((code) => this.lookupService.getLookupName(docTypeList, code) || code);
        this.notificationService.error('Required documents missing', `Please upload a supporting document for each parent/guardian: ${labels.join(', ')}`);
        return;
      }
    }

    const {
      infoData,
      contactDetailsData,
      serviceDetailsData,
      commPreferencesData,
      filesUploaded
    } = this.referralDataService.getReferralFormData();
    console.log('FORM DATA ===== ', this.referralDataService.getReferralFormData());
    const updatedServiceDetailsData =
      serviceDetailsData && this.formatServiceDetailsData(serviceDetailsData);
    const updatedCommPreferncesData =
      commPreferencesData &&
      this.formatCommPreferencesData(commPreferencesData);
    const updatedInfoData = infoData && this.formatInfoData(infoData);
    const updatedContactDetailsData =
      contactDetailsData && this.formatContactDetailsData(contactDetailsData);
    // const { SSN, CIN, ...formattedInfoData } = updatedInfoData;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { email, altPhone, mobile, preferredMethod, ...formattedcontactDetails } = updatedContactDetailsData;

    let dataToSave;

    if (flow == 'participants') {
      const updatedInfo = {
        ...updatedInfoData,
        state: this.referralDataService.getReferralFormData().state.state,
        ...(actionPerformed ? { action: actionPerformed } : {}),
      };
      dataToSave = {
        infoData: updatedInfo,
        communicationData: formattedcontactDetails,
        contactDetailsData: updatedCommPreferncesData,
      };
    } else if (flow == 'providers') {
      const { servicesRendered, ...updatedData } = updatedInfoData;
      const updatedInfo = {
        ...updatedData,
        state: this.referralDataService.getReferralFormData().state.state,
        ...(actionPerformed ? { action: actionPerformed } : {}),
      };
      const updatedService = {
        ...updatedServiceDetailsData,
        servicesRendered: updatedInfoData.servicesRendered?.map(
          (service: string) => {
            if (service === "OTHE") {
              return { serviceType: service, otherTypeDesc: updatedInfo.servicesRenderedAdditionalDetails }
            } else {
              return { serviceType: service }
            }
          }),
      }

      dataToSave = {
        infoData: updatedInfo,
        serviceDetailsData: updatedService,
        communicationData: formattedcontactDetails,
        contactDetailsData: updatedCommPreferncesData,
      };
    }
    else if (flow === 'participantEnrollment') {
      const updatedInfo = {
        ...updatedInfoData,
        programCode: this.program.program_code,
        residentGt18: formattedcontactDetails.residentGt18,
        applicationStage:
          this.referralDataService.getReferralDetails('infoData')
            .applicationStage,
        participantId:
          this.storageService.getParticipantId(),
        applicationSource:
          this.referralDataService.getReferralDetails('infoData')
            .applicationSource,
        trackingNumber: this.storageService.getTrackingNumber(),
        participantPPLId: this.storageService.getParticipantPPLId(),
        finalApplicantDisposition:
          this.referralDataService.getReferralDetails('infoData').finalApplicantDisposition,
        isRegistrationLinkSent: this.referralDataService.getReferralDetails('infoData').isRegistrationLinkSent || false,
        ...(actionPerformed ? { action: actionPerformed } : {}),
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { residentGt18, yearsInState, ...filteredContactDetails } = formattedcontactDetails;
      const files = filesUploaded?.filesUploaded.map((file: any) => ({
        referralFileId: file.id,
      }));

      dataToSave = {
        infoData: updatedInfo,
        communicationData: filteredContactDetails,
        contactDetailsData: updatedCommPreferncesData,
        filesUploaded: files || [],
      };
    }
    else if (flow === 'providerEnrollment') {
      const providerTypes = this.sectionsService.getEnrollmentType();
      updatedInfoData["providerType"] = ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS[providerTypes[0]];
      const updatedInfo = {
        ...updatedInfoData,
        programCode: this.program.program_code,
        providerTypeName: ENROLLMENT_TITLE_MAPPINGS[providerTypes[0]],
        isRegistrationLinkSent: this.referralDataService.getReferralDetails('infoData').isRegistrationLinkSent || false,
        providerId: this.storageService.getProviderId(),
        trackingNumber: this.storageService.getTrackingNumber(),
        providerPPLId: this.storageService.getProviderPPLId(),
        ...(actionPerformed ? { action: actionPerformed } : {}),
      };
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { residentGt18, yearsInState, ...filteredContactDetails } = formattedcontactDetails;

      // const files = filesUploaded?.filesUploaded.map((file: any) => ({
      //   referralFileId: file.id,
      // }));

      dataToSave = {
        pageType: "providerCreateWizard",
        infoData: updatedInfo,
        communicationData: filteredContactDetails,
        serviceDetailsData: updatedServiceDetailsData,
        contactDetails: updatedCommPreferncesData
      };
    }
    else {
      const updatedInfo = {
        ...updatedInfoData,
        programCode: this.program.program_code,
        residentGt18: formattedcontactDetails.residentGt18,
        applicationStage:
          this.referralDataService.getReferralDetails('infoData')
            .applicationStage,
        referralId:
          this.referralDataService.getReferralDetails('infoData').referralId,
        applicationSource:
          updatedInfoData.applicationSource || this.referralDataService.getReferralDetails('infoData')
            .applicationSource,
        trackingNumber:
          this.referralDataService.getReferralDetails('infoData')
            .trackingNumber,
        ...(actionPerformed ? { action: actionPerformed } : {}),
      };
      const { residentGt18, yearsInState, ...filteredContactDetails } =
        formattedcontactDetails;
      const files = filesUploaded?.filesUploaded.map((file: any) => ({
        referralFileId: file.id,
      }));

      dataToSave = {
        infoData: updatedInfo,
        communicationData: filteredContactDetails,
        contactDetailsData: updatedCommPreferncesData,
        filesUploaded: files || [],
      };
    }

    console.log('final Data to save', dataToSave);

    this.referralDataService.saveLead(flow, dataToSave, true).subscribe(
      (resp) => {
        if (resp.status == 200 && resp.responsedata?.trackingNumber) {
          // this.router.navigate(['/confirmation'], {
          //   state: { num: trackingNumber, id: id },
          // });
          this.showConfirmation.emit({
            trackingNumber: resp.responsedata.trackingNumber,
            id: resp.responsedata.participantId || resp.responsedata.providerId || resp.responsedata.referralId,
            pplId: this.storageService.getParticipantPPLId() || this.storageService.getProviderPPLId(),
            isApproved: actionPerformed == 'SUBMIT_APPROVE',
          });
          this.referralDataService.clearReferrralData();
        } else {
          if (resp.status !== 200) {
            this.notification.error('API Call Failed', 'Please try again later.');
          }
          else {
            return;
          }
        }
      },
      (error) => {
        console.error('Error while submitting lead sumbmission: ', error);
      }
    );
  }

  getadditionalAttributes(values: any, attributeMap: any) {
    return Object.entries(attributeMap)
      .filter(([key]) => values[key] !== undefined && values[key] !== null)
      .map(([key, attributeId]) => ({
        additionalAttributesId: attributeId,
        value: String(values[key])
      }));
  }

  getIdentifiers(values: any, lookupArray: any[]) {
    const referralIdentifiers =
      this.referralDataService.getReferralDetails('infoData').identifiers;
    const enrollmentTypes = this.sectionsService.getEnrollmentType() ?? [];
    const isEmployerEnrollment = this.flow === 'providerEnrollment' && enrollmentTypes.includes('employer');
    let ids: string[];
    if (this.flow === 'participantEnrollment') {
      ids = ['SSN', 'CIN', 'ITIN'];
    } else if (isEmployerEnrollment) {
      ids = ['SSN', 'NPI', 'CIN', 'TIN', 'ITIN', 'EINES'];
    } else {
      ids = ['SSN', 'EIN', 'NPI', 'CIN', 'TIN', 'ITIN'];
    }
    return lookupArray
      .filter((lookup) => ids.includes(lookup.lkpValueCode))
      .map((lookup) => {
        const value = lookup.lkpValueCode === 'EINES'
          ? (values['EIN'] || '')
          : (values[lookup.lkpValueCode] || '');
        const match = referralIdentifiers?.find(
          (ref: any) =>
            ref.type === lookup.lkpValueCode &&
            ref.value === value
        );
        const result: any = {
          type: lookup.lkpValueCode,
          value,
        };
        if (match) {
          result.referralIdentifierId = match.referralIdentifierId;
        }
        return result;
      });
  }

  getContactMethods(values: any, lookupArray: any[]) {
    return lookupArray.map((lookup) => {
      return {
        contactMethod: lookup.lkpValueCode,
        value: values[lookup.lkpValueCode] || '',
        preferred:
          values.preferredMethod.includes(lookup.lkpValueCode) ? 'true' : 'false',
        isSMSConstentGiven: lookup.lkpValueCode === "mobile" && values.isSMSConstentGiven ? 'true' : 'false',
      };
    });
  }
}
